// Branch semantics, in one place.
//
// Storage: a connection carries `branch_parent_uid` + `branch_mode`.
//   "sub"    → the person hangs off the parent (extended network, one hop out)
//   "direct" → still your degree-1 contact, tagged "met via" the parent
// Only "sub" nests in the connection tree; both overlay onto the graph.
//
// Consumers (tree, detail panel, dashboard, save marshaling) read a connection
// through `branchOf` and render with `branchLabel` instead of decoding the raw
// strings themselves. `branchWriteFields` is the inverse — it turns a
// BranchPicker value into the fields to persist.

/**
 * Interpret a connection's branch fields.
 * @returns {{ kind: "hangs-off"|"met-via"|null, parentUid: string|null, parentName: string }}
 */
export function branchOf(conn) {
  const parentUid = conn?.branch_parent_uid || null;
  if (!parentUid) return { kind: null, parentUid: null, parentName: "" };
  // Match existing UI: only an explicit "sub" hangs off; anything else is "met via".
  const kind = conn.branch_mode === "sub" ? "hangs-off" : "met-via";
  return { kind, parentUid, parentName: conn.branch_parent_name || "" };
}

/** Human label for a branch, or null when the connection branches off you. */
export function branchLabel(branch) {
  if (!branch?.kind) return null;
  const name = branch.parentName || "a connection";
  return branch.kind === "hangs-off" ? `Branches off ${name}` : `Met via ${name}`;
}

/**
 * Turn a BranchPicker value ({ parentUid, parentName, mode }) into the Firestore
 * fields to write. A null parent clears the branch.
 */
export function branchWriteFields(picker) {
  if (!picker?.parentUid) {
    return { branch_parent_uid: null, branch_parent_name: "", branch_mode: null };
  }
  return {
    branch_parent_uid: picker.parentUid,
    branch_parent_name: picker.parentName || "",
    branch_mode: picker.mode || "sub",
  };
}
